/**
 * Merchant identity in the catalog: the monogram, and which tile it sits on.
 *
 * Shared rather than written twice, because both clients render the same
 * storefront and a merchant whose tile is teal on the web and amber in the app
 * is not one brand — it is a bug the eye notices before anyone can say why.
 * That is the same failure the palette had; see `apps/web/src/palette.test.ts`.
 *
 * No imports, deliberately. This is reached from `apps/mobile` through the
 * `/brand` subpath for the same reason `/money` exists: the package root pulls
 * in `@sinclair/typebox`, which cannot be in a Metro bundle. Keep this file
 * dependency-free.
 *
 * ---------------------------------------------------------------------------
 * These are placeholders, and are built to be replaced
 * ---------------------------------------------------------------------------
 *
 * What actually makes a cashback storefront work is the merchant's own logo — a
 * wall of marks people already recognise. We have no logo pipeline yet: the
 * network adapters expose `parsePostback`, `confirm` and `settlements` and
 * nothing that lists a catalog, so no image ever arrives.
 *
 * So a tile is a coloured square with an initial on it, at exactly the size and
 * shape a logo will occupy. When `logoUrl` becomes non-null the image replaces
 * the monogram and no layout moves.
 */
/**
 * The tile colours, by index. Also declared in `styles.css` as `--tile-1…6` so
 * the stylesheet stays a complete statement of the palette; the two are checked
 * against each other by `apps/web/src/palette.test.ts`. Neither client copies
 * them — both call `tileColourFor`.
 *
 * Six, all mid-dark and saturated, all at or above 4.5:1 against white — they
 * carry a white monogram, and they are the same in light and dark because a
 * brand tile is not a surface that follows the reader's scheme. Two of them
 * (`#3b6fe0`, `#047857`) are already the accent and the light-mode positive, so
 * the set reads as an extension of the palette rather than an escape from it.
 */
export const TILE_COLOURS = [
    '#3b6fe0', // blue    — the brand accent
    '#047857', // green   — the light-mode positive
    '#0f766e', // teal
    '#7c3aed', // violet
    '#b45309', // amber
    '#be123c', // rose
];
/**
 * Which tile a merchant gets, from its slug.
 *
 * Keyed on the slug rather than the display name because the slug is the stable
 * identifier — `merchants.slug` is unique and does not change when someone
 * fixes the capitalisation of a legal name, and a merchant whose colour moved
 * overnight looks like a different shop.
 *
 * FNV-1a, which is not a security choice — it is here because a plain character
 * sum gives anagrams and short names a visibly uneven spread, and a run of
 * adjacent merchants in the same colour is the thing this is trying to avoid.
 */
export function tileIndexFor(slug) {
    let hash = 0x811c9dc5;
    for (let i = 0; i < slug.length; i += 1) {
        hash ^= slug.charCodeAt(i);
        // `Math.imul` keeps this in 32-bit territory. Plain `*` would go through
        // doubles and lose the low bits that carry the variation.
        hash = Math.imul(hash, 0x01000193);
    }
    return Math.abs(hash) % TILE_COLOURS.length;
}
/** The tile colour for a merchant. */
export function tileColourFor(slug) {
    return TILE_COLOURS[tileIndexFor(slug)];
}
/**
 * Corporate forms, dropped from the end of a name before initialising it.
 *
 * **Only from the end.** Dropping them anywhere would turn "As Seen On TV" into
 * SO and "Co-op Travel" into T, because `as` and `co` are both on this list.
 * A suffix is a suffix by position, not by spelling.
 */
const SUFFIXES = new Set([
    'inc', 'llc', 'ltd', 'co', 'corp', 'corporation', 'company', 'holdings',
    // European and Asian forms, which are common enough in an affiliate catalog
    // to matter: Zalando SE and Airbus SE would otherwise monogram as ZS and AS.
    'gmbh', 'plc', 'sa', 'ag', 'bv', 'nv', 'se', 'ab', 'as', 'oy', 'spa',
    'sarl', 'pty', 'pte', 'kk', 'kg', 'srl', 'aps',
]);
/**
 * One or two letters standing in for a logo.
 *
 * Two where the name has two words worth initialising, one otherwise — "Dunhill
 * Direct" reads better as DD than as D, and a single tall letter beats a
 * cramped pair for a one-word name.
 *
 * Trailing corporate forms go first, so "Northlight Outfitters, Inc." is NO and
 * not NI: the reader is looking for the shop, and "Inc" is not part of what
 * they are looking for.
 */
export function monogramFor(name) {
    const words = name
        // Hyphens and slashes split too, so "7-Eleven" is 7E rather than 7 and
        // "T-Mobile" is TM. A hyphenated name is two words wearing one coat.
        .split(/[\s,/–—-]+/)
        .map((word) => word.replace(/[^\p{L}\p{N}]/gu, ''))
        .filter((word) => word.length > 0);
    // Trailing suffixes only — see the note on SUFFIXES. "Acme Holdings Ltd"
    // sheds both and leaves A.
    while (words.length > 1 && SUFFIXES.has((words.at(-1) ?? '').toLowerCase())) {
        words.pop();
    }
    const initials = words
        .map((word) => word[0])
        .filter((letter) => letter !== undefined)
        .slice(0, 2)
        .join('')
        .toUpperCase();
    // Everything was punctuation or a suffix. A tile with nothing on it looks
    // broken, so fall back to the first character of the original name.
    return initials.length > 0 ? initials : (name.trim()[0] ?? '?').toUpperCase();
}
/**
 * A cashback rate, as people read it: `480` → `4.8%`, `500` → `5%`.
 *
 * Up to two decimals, with trailing zeros trimmed. Both bounds matter:
 *
 * **Two, not one**, because the tile shows the percentage above a worked
 * example and one decimal makes them disagree. 336 bps rounds to "3.4%", which
 * beside "$3.36 back per $100" invites the reader to check the arithmetic and
 * find it wrong. It is not wrong; the label was.
 *
 * **Trimmed**, because "5.00%" on a storefront tile reads as a spreadsheet, and
 * the whole reason this number is set at 26px is that it is supposed to be
 * read at a glance rather than audited.
 */
export function formatRateBps(bps) {
    const percent = bps / 100;
    const fixed = percent.toFixed(2);
    const trimmed = fixed.includes('.') ? fixed.replace(/0+$/, '').replace(/\.$/, '') : fixed;
    return `${trimmed}%`;
}
//# sourceMappingURL=brand.js.map